import EditGeneralDetailsForm from "../components/UI/Forms/Wizard Forms/EditForms/EditGeneralDetailsForm";
import { MessageEntity } from "../entities/UIEntities/MessageEntity";
import { CarOperationsEntity } from "../entities/ServicesEntities/UserEntities/CarOperationsEntity";
import { UserSiteActivationEntity } from "../entities/ServicesEntities/UserEntities/UserSiteActivationEntity";
import { commonActions } from "../redux/reducers/CommonReducer";
import store from "../redux/Store";
import Icons from "../resources/icons/Icons";
import Strings from "../resources/Strings";
import * as actionsService from "../services/ActionsService/ActionsService";
import * as carActionsService from "../services/ActionsService/CarActionsService";
import * as userSiteActionsService from "../services/ActionsService/UserSiteActionsService";
import * as userScopesActionsService from "../services/ActionsService/UserScopesActionsService";
import * as billingActionsService from "../services/ActionsService/BillingActionsService";
import * as messagingActionsService from "../services/ActionsService/MessagingActionsService";
import { showErrorMessage, showUpdateSuccessMessage, showDeletedParkerSuccessMessage, showDeletedBillingRuleSuccessMessage, showDeletedChargeAccountSuccessMessage } from "./MessagesUtils";
import { userActions } from "../redux/reducers/UserReducer";
import { messagingActions } from "../redux/reducers/MessagingReducer";
import AddCarsForm from "../components/UI/Forms/Wizard Forms/AddForms/AddCarsForm";
import AddFacilityForm from "../components/UI/Forms/Wizard Forms/AddForms/AddFacilityForm";
import { SiteOperationsEntity } from "../entities/ServicesEntities/UserEntities/SiteOperationsEntity";
import uploadFileToBlob, {
  baseStoragePath,
} from "../services/Azure/azure-storage-blob";
import { createGuid } from "./CommonUtils";
import AddParkerAllocationsForm from "../components/UI/Forms/Wizard Forms/AddForms/AddParkerAllocationsForm";
import EditGmtDetailsForm from "../components/UI/Forms/Wizard Forms/EditForms/EditGmtDetailsForm";
import AddPermissionsForm from "../components/UI/Forms/Wizard Forms/AddForms/AddPermissionsForm";
import DisactiveMessageModal from "../components/UI/Modals/MessageModal/DisactiveMessageModal";
import AddBillingRuleForm from "../components/UI/Forms/Wizard Forms/AddForms/AddBillingRuleForm";
import AddEVRuleForm from "../components/UI/Forms/Wizard Forms/AddForms/AddEVRuleForm";
import { billingActions } from "../redux/reducers/BillingReducer";
import ParkerDisactiveMessageModal from "../components/UI/Modals/MessageModal/ParkerDisactiveMessageModal";
import { MENU_ITEMS } from "../enums/Enums";
import AddChargeAccountForm from "../components/UI/Forms/Wizard Forms/AddForms/AddChargeAccountForm";
import AddCampaignForm from "../components/UI/Forms/Wizard Forms/AddForms/AddCampaignForm";
import AddMailingListForm from "../components/UI/Forms/Wizard Forms/AddForms/AddMailingListForm";
import { getMailingListData, getMessagingCampaignsData } from "./MessagingUtils";
import { refreshSingleUserData, refreshSingleUserDataAndExtended } from "../services/ParkerService";

/**
 * Refreshes sites and scopes after facility operations (assign/unassign)
 * @param {Array} updatedSites - The updated sites array from API
 * @param {string} userId - Optional user ID to refresh single user data
 * @returns {Promise} - Resolves when all updates complete
 */
export const refreshSitesAndScopes = (updatedSites, userId = null) => {
  return new Promise((resolve, reject) => {
    const selectedScope = store.getState().user?.selectedScope;
    if (!selectedScope) {
      reject(new Error('No selected scope found'));
      return;
    }
    
    // 1. Update sites in Redux
    store.dispatch(userActions.setSites(updatedSites));
    
    // 2. Fetch fresh scopes to include/exclude facility changes
    actionsService.getScopeByScopeId(selectedScope.id, true, false)
      .then((scopeResponse) => {
        const freshScopes = scopeResponse.data.entity;
        
        // 3. Update currentUsersScopes with fresh data
        store.dispatch(userActions.setCurrentUsersScopes(freshScopes));
        
        // 4. Recalculate selectedSites with fresh scope data
        store.dispatch(userActions.setUsersScope(selectedScope.id));
        
        // 5. Optionally refresh single user data WITH updated scopes
        if (userId) {
          // Pass the fresh scopes to ensure API calls use updated context
          const scopesIds = freshScopes.map(scope => scope.id);
          return refreshSingleUserDataAndExtended(userId, scopesIds, freshScopes, true, false);
        }
        return Promise.resolve();
      })
      .then(() => {
        resolve();
      })
      .catch((error) => {
        console.error('Error refreshing sites and scopes:', error);
        reject(error);
      });
  });
};

export const handleEditEvRule = (billingData) => {
  store.dispatch(
    commonActions.setSideModal({
      modalContent:
        <AddEVRuleForm
          isEditMode={true}
          {...billingData?.row}
        />,
    })
  );
};

export const handleEditBillingRule = (billingData) => {
  store.dispatch(
    commonActions.setSideModal({
      modalContent:
        <AddBillingRuleForm
          isEditMode={true}
          {...billingData?.row}
        />,
    })
  );
};

export const handleDeleteBillingRule = (billingData) => {
  const deleteBillingRuleMessage = new MessageEntity(
    Icons.DELETE_ITEM_3,
    Strings.TITLES.DELETE_BILLING_RULE,
    `${Strings.DELETE_MESSAGE}`.format(
      `${billingData?.row?.ruleName}`
    ),
    Strings.IM_SURE,
    Strings.CANCEL,
    deleteRule.bind(this, billingData)
  );
  store.dispatch(commonActions.setMessageModal(deleteBillingRuleMessage));
};

export const handleEditGeneralDetails = (userData) => {
  store.dispatch(
    commonActions.setSideModal({
      modalContent: <EditGeneralDetailsForm {...userData} />,
    })
  );
};

export const handleEditGmtDetails = (userData) => {
  store.dispatch(
    commonActions.setSideModal({
      modalContent: <EditGmtDetailsForm {...userData} />,
    })
  );
};

export const handleEditChargeAccountDetails = (userData) => {
  store.dispatch(
    commonActions.setSideModal({
      modalContent: <AddChargeAccountForm isEditMode={true} {...userData} />,
    })
  );
};

export const handleAddCars = (userData) => {
  store.dispatch(
    commonActions.setSideModal({
      modalContent: <AddCarsForm {...userData} />,
    })
  );
};

export const handleUserActivationChange = (userData) => {
  const userSiteActivationEntity = new UserSiteActivationEntity(
    userData.userSiteActivation.id,
    !userData.userSiteActivation.isActive
  );
  userSiteActionsService
    .updateUserSiteActivation(userSiteActivationEntity)
    .then((response) => {
      if (response.data.isSuccess) {
        // actionsService.getUserSites().then((response) => {
        //   store.dispatch(userActions.setSites(response.data.entity));
        // });
        store.dispatch(userActions.setUsersScope(userData.selectedScope.id));
        const userId = userData?.utronUser?.id;
        if (userId) {
          refreshSingleUserData(userId);
        }
        showUpdateSuccessMessage();
      } else {
        showErrorMessage(response.data?.error);
      }
    });
};

export const handleParkerActivation = (userData) => {
  const handleParkerActivationMessage = new MessageEntity(
    Icons.NEW_PARKER,
    MENU_ITEMS.PARKER_ACTIVATION,
    Strings.PARKER_ACTIVATION_MESSAGE,
    null, 
    null, 
    null, 
    null,
    <ParkerDisactiveMessageModal userData={userData} />
  );
  store.dispatch(commonActions.setMessageModal(handleParkerActivationMessage));
};

export const handleEditCar = (carsData) => {
  store.dispatch(
    commonActions.setSideModal({
      modalContent: (
        <AddCarsForm
          isEditMode={true}
          carUserTokens={carsData?.car?.carUserTokens}
          utronUser={carsData?.car?.utronUser}
          parentSite={carsData?.parentSite}
          {...carsData}
        />
      ),
    })
  );
};

export const handleDeleteCar = (carsTokenData) => {
  const deleteCarMessage = new MessageEntity(
    Icons.DELETE_ITEM_3,
    Strings.TITLES.DELETE_CAR,
    `${Strings.DELETE_MESSAGE}`.format(
      `${carsTokenData?.car.licensePlate} ${carsTokenData?.car.manufacturer === null ? '' : carsTokenData?.car.manufacturer}`
    ),
    Strings.IM_SURE,
    Strings.CANCEL,
    deleteCar.bind(this, carsTokenData)
  );
  store.dispatch(commonActions.setMessageModal(deleteCarMessage));
};

export const handleAddFacilityToUser = (userData) => {
  store.dispatch(
    commonActions.setSideModal({
      modalContent: <AddFacilityForm isEditMode={true} {...userData} />,
    })
  );
};

export const handleUnassignFacility = (facillityData) => {
  const unassignFacilityMessage = new MessageEntity(
    Icons.DELETE_ITEM_3,
    Strings.TITLES.UNASSIGN_FACILITY,
    `${Strings.UNASSIGN_MESSAGE}`.format(facillityData.site.displayName),
    Strings.IM_SURE,
    Strings.CANCEL,
    unassignFacility.bind(this, facillityData)
  );
  store.dispatch(commonActions.setMessageModal(unassignFacilityMessage));
};

export const handleTokenOccupied = (userName, confirmFn, cancelFn) => {
  const tokenOccupiedMessage = new MessageEntity(
    Icons.TOKEN_OCCUPIED,
    Strings.TITLES.TOKEN_OCCUPIED,
    `${Strings.TOKEN_OCCUPIED_MESSAGE}`.format(userName),
    Strings.IM_SURE,
    Strings.CANCEL,
    confirmFn,
    cancelFn
  );
  store.dispatch(commonActions.setMessageModal(tokenOccupiedMessage));
};
export const handleDeleteUser = (userData) => {
  const deleteUserMessage = new MessageEntity(
    Icons.DELETE_ITEM_3,
    Strings.TITLES.DELETE_USER,
    `${Strings.DELETE_MESSAGE}`.format(
      `${userData.firstName} ${userData.lastName}`
    ),
    Strings.IM_SURE,
    Strings.CANCEL,
    deleteUser.bind(this, userData?.id)
  );
  store.dispatch(commonActions.setMessageModal(deleteUserMessage));
};
export const handleDeleteChargeAccountUser = (userData) => {
  const deleteUserMessage = new MessageEntity(
    Icons.DELETE_ITEM_3,
    Strings.TITLES.DELETE_USER,
    `${Strings.DELETE_MESSAGE}`.format(
      `${userData?.chargeAccount?.name}`
    ),
    Strings.IM_SURE,
    Strings.CANCEL,
    deleteChargeAccountUser.bind(this, userData?.id)
  );
  store.dispatch(commonActions.setMessageModal(deleteUserMessage));
};
export const deleteRule = (billingData) => {
  const ruleId = billingData.id;
  if (ruleId) {
    billingActionsService.deleteBillingRule(ruleId).then((response) => {
      // store.dispatch(commonActions.closeSideModal());
      if (response.data.isSuccess) {
        store.dispatch(billingActions.setBillingRules(response.data.entity));
        showDeletedBillingRuleSuccessMessage();
      } else {
        showErrorMessage(response.data?.error);
      }
    });
  }
};

export const deleteCar = (carsTokenData) => {
  const carId = carsTokenData?.car?.id;
  const userId = carsTokenData?.car?.userId;
  //const siteId = carsTokenData?.siteId;
  const siteId = carsTokenData?.car?.assignCarToken?.siteId; // my add
  const carOperationsEntity = new CarOperationsEntity(carId, userId, siteId);
  if (carId) {
    carActionsService.deleteCar(carOperationsEntity, true, false).then((response) => {
      if (response.data.isSuccess) {
        return actionsService
          .getUserSites(true, false)
          .then((response) => {
            store.dispatch(userActions.setSites(response.data.entity));
            store.dispatch(userActions.setUserCarTokens(response.data.entity));
            if (userId) {
              return refreshSingleUserDataAndExtended(userId, null, null, true, false);
            }
            return Promise.resolve();
          });
      } else {
        store.dispatch(commonActions.setIsShowLoadingSpinner(false));
        showErrorMessage(response.data?.error);
        return Promise.reject(response.data?.error);
      }
    })
    .then(() => {
      store.dispatch(commonActions.setIsShowLoadingSpinner(false));
      showUpdateSuccessMessage();
    })
    .catch((error) => {
      store.dispatch(commonActions.setIsShowLoadingSpinner(false));
      console.error('Error refreshing user data after deleting car:', error);
    });
  }
};
export const deleteUser = (userId) => {
  actionsService.deleteUser(userId, true, true).then((response) => {
    if (response.data.isSuccess) {
      store.dispatch(userActions.deleteParker(userId));
      store.dispatch(userActions.setShowUserInfoWindow(false));
      actionsService.getUserSites(true, false)
        .then((sitesResponse) => {
          store.dispatch(userActions.setSites(sitesResponse.data.entity));
        })
        .then(() => {
          store.dispatch(userActions.setParkersAndGuests());
          showDeletedParkerSuccessMessage();
        })
        .catch((error) => {
          console.error('Error refreshing sites after user deletion:', error);
          store.dispatch(userActions.setParkersAndGuests());
          showDeletedParkerSuccessMessage();
        });
    } else {
      showErrorMessage(response.data?.error);
    }
  });
};
export const deleteChargeAccountUser = (userId) => {
  actionsService.deleteChargeAccountUser(userId, true, true).then((response) => {
    if (response.data.isSuccess) {
      store.dispatch(userActions.deleteChargeAccount(userId));
      store.dispatch(userActions.setShowUserInfoWindow(false));
      showDeletedChargeAccountSuccessMessage();
    } else {
      showErrorMessage(response.data?.error);
    }
  });
};
export const unassignFacility = (facillityData) => {
  const userType = facillityData?.utronUser?.userToUserTypes?.find(
    (userToUserTypes) =>
      userToUserTypes.userType.siteId === facillityData.site.id
  );
  const siteOperationsEntity = new SiteOperationsEntity(
    userType?.id,
    facillityData?.site.id,
    facillityData?.utronUser?.id,
    userType?.userType?.id
  );
  
  userSiteActionsService
    .unassignUserToSite(siteOperationsEntity, true, false)
    .then((response) => {
      if (response.data.isSuccess) {
        return actionsService.getUserSites(true, false)
          .then((sitesResponse) => {
            return refreshSitesAndScopes(sitesResponse.data.entity, facillityData?.utronUser?.id);
          });
      } else {
        showErrorMessage(response.data?.error);
        store.dispatch(commonActions.setIsShowLoadingSpinner(false));        
        return Promise.reject(response.data?.error);
      }
    })
    .then(() => {
      showUpdateSuccessMessage();
      store.dispatch(commonActions.setIsShowLoadingSpinner(false));      
    })
    .catch((error) => {
      store.dispatch(commonActions.setIsShowLoadingSpinner(false));
      console.error('Error during unassign facility:', error);
    });
};
export const errorAction = () => {
  store.dispatch(commonActions.closeMessageModal());
};

export const reloadAction = () => {
  window.location.reload(true);
};

export const handleAddParkingAllocations = (userData) => {
  // store.dispatch(
  //   commonActions.setSideModal({
  //     modalContent: (
  //       <AddParkingAllocationsForm isEditMode={true} userData={userData} />
  //     ),
  //   })
  // );

  store.dispatch(
    commonActions.setSideModal({
      modalContent: (
        <AddParkerAllocationsForm isEditMode={true} userData={userData} />
      ),
    })
  );
};

export const handleValidateParking = (confirmFn) => {
  const validateParkingMessage = new MessageEntity(
    Icons.ERROR,
    Strings.TITLES.VALIDATIONS_PAGE,
    Strings.ARE_YOU_SURE,
    Strings.IM_SURE,
    Strings.CANCEL,
    confirmFn
  );
  store.dispatch(commonActions.setMessageModal(validateParkingMessage));
};

export const handleTokenActivation = (allocationData, confirmFn) => {
  const activationTextModeTitle = allocationData?.isActive ? "Deactivate" : "Activate";
  const activationTextModeMessage = allocationData?.isActive ? "deactivate" : "activate";
  const tokenOccupiedMessage = new MessageEntity(
    Icons.DELETE_ITEM_3,
    `${Strings.TITLES.ACTIVATION_TOKEN}`.format(`${activationTextModeTitle}`),
    `${Strings.TOKEN_ACTIVATION_MESSAGE}`.format(`${activationTextModeMessage}`),
    Strings.IM_SURE,
    Strings.CANCEL,
    confirmFn
  );
  store.dispatch(commonActions.setMessageModal(tokenOccupiedMessage));
};

export const handleDeleteToken = (confirmFn) => {
  const tokenOccupiedMessage = new MessageEntity(
    Icons.DELETE_ITEM_3,
    Strings.TITLES.DELETE_TOKEN,
    `${Strings.DELETE_TOKEN_MESSAGE}`,
    Strings.IM_SURE,
    Strings.CANCEL,
    confirmFn
  );
  store.dispatch(commonActions.setMessageModal(tokenOccupiedMessage));
};

// activate / deactivate allocation
export const handleFobActivation = (allocationData, confirmFn) => {
  const activationTextModeTitle = allocationData?.isActive ? "Deactivate" : "Activate";
  const activationTextModeMessage = allocationData?.isActive ? "deactivate" : "activate";
  const tokenOccupiedMessage = new MessageEntity(
    Icons.DELETE_ITEM_3,
    `${Strings.TITLES.ACTIVATION_ALLOCATION}`.format(`${activationTextModeTitle}`),
    `${Strings.ACTIVATION_ALLOCATION_MESSAGE}`.format(`${activationTextModeMessage}`),
    Strings.IM_SURE,
    Strings.CANCEL,
    confirmFn
  );
  store.dispatch(commonActions.setMessageModal(tokenOccupiedMessage));
};

export const handleResetFob = (confirmFn) => {
  const resetFobMessage = new MessageEntity(
    Icons.ERROR,
    Strings.TITLES.RESET_FOB,
    Strings.RESET_FOB_MESSAGE,
    Strings.IM_SURE,
    Strings.CANCEL,
    confirmFn
  );
  store.dispatch(commonActions.setMessageModal(resetFobMessage));
};

export const handleSkipFob = (confirmFn) => {
  const skipFobMessage = new MessageEntity(
    Icons.ERROR,
    Strings.TITLES.SKIP_FOB,
    Strings.SKIP_FOB_MESSAGE,
    Strings.IM_SURE,
    Strings.CANCEL,
    confirmFn
  );
  store.dispatch(commonActions.setMessageModal(skipFobMessage));
};

export const handleRemoveParkerAllocation = (confirmFn) => {
  const tokenOccupiedMessage = new MessageEntity(
    Icons.DELETE_ITEM_3,
    Strings.TITLES.REMOVE_PARKER_ALLOCATION,
    `${Strings.REMOVE_PARKER_ALLOCATION_MESSAGE}`,
    Strings.IM_SURE,
    Strings.CANCEL,
    confirmFn
  );
  store.dispatch(commonActions.setMessageModal(tokenOccupiedMessage));
};

export const handleUserPermissions = (userData) => {
  store.dispatch(
    commonActions.setSideModal({
      modalContent: <AddPermissionsForm userData={userData} />,
    })
  );
};

export const handleEditPermissions = (userData) => {
  store.dispatch(
    commonActions.setSideModal({
      modalContent: (
        <AddPermissionsForm isEditMode={true} userData={userData} />
      ),
    })
  );
};

export const handlePermissionActivation = (userData) => {
  let subTitleTextToShow = "";
  if (userData?.isActivateUserMenuItem) {
    if (!userData?.isNonProject) {
      // project case
      subTitleTextToShow = `${Strings.ACTIVATE_PERMISSION_MESSAGE}`.format(
        `${userData?.utronUser?.firstName} ${userData?.utronUser?.lastName}`
      );
    }
    else {
      // non-project
      subTitleTextToShow = `${Strings.ACTIVATE_PERMISSION_ONE_SCOPE_MESSAGE}`.format(
        `${userData?.utronUser?.firstName} ${userData?.utronUser?.lastName}`
      );
    }
  }
  else {
    if (userData?.isNonProject) {
      subTitleTextToShow = `${Strings.DISACTIVE_PERMISSION_MESSAGE}`.format(
        `${userData?.utronUser?.firstName} ${userData?.utronUser?.lastName}`
      );
    }
  }



  const disactiveUserMessage = new MessageEntity(
    userData?.isActivateUserMenuItem ? Icons.ADD_ROLE : Icons.DISACTIVATE, // Icon
    userData?.isActivateUserMenuItem ? 
    `${Strings.TITLES.ACTIVATE_PERMISSION}`.format(
      `${userData?.utronUser?.firstName} ${userData?.utronUser?.lastName}`
    ) :
    `${Strings.TITLES.DISACTIVE_PERMISSION}`.format(
      `${userData?.utronUser?.firstName} ${userData?.utronUser?.lastName}`
    ),
    subTitleTextToShow,
    // userData?.isActivateUserMenuItem && userData?.isNonProject ? 
    // `${Strings.ACTIVATE_PERMISSION_MESSAGE}`.format(
    //   `${userData?.utronUser?.firstName} ${userData?.utronUser?.lastName}`
    // ) :
    //  `${Strings.DISACTIVE_PERMISSION_MESSAGE}`.format(
    //   `${userData?.utronUser?.firstName} ${userData?.utronUser?.lastName}`
    // ),
    null,
    null,
    null,
    null,
    <DisactiveMessageModal userData={userData} />
  );
  store.dispatch(commonActions.setMessageModal(disactiveUserMessage));
};

export const handleDeletePermission = (userData) => {
  const deletePermissionMessage = new MessageEntity(
    Icons.DELETE_ITEM_3,
    Strings.TITLES.DELETE_PERMISSION,
    `${Strings.DELETE_PERMISSION_MESSAGE}`.format(`${userData.scope.name}`,
      `${userData?.utronUser?.firstName} ${userData?.utronUser?.lastName}`
    ),
    Strings.IM_SURE,
    Strings.CANCEL,
    deletePermission.bind(this, userData?.userScopeId)
  );
  store.dispatch(commonActions.setMessageModal(deletePermissionMessage));
};

export const deleteMailingList = (listData) => {
  messagingActionsService.deleteMailingList(listData.id).then((response) => {
    if (response.data.isSuccess) {
      // update all mailing lists
      // update mailing list
      let mailingListsData = getMailingListData(response.data.entity, listData?.site?.[0]);
      store.dispatch(messagingActions.setMailingListsData(mailingListsData));
      store.dispatch(messagingActions.setMessagingSelectionModel(null));

      // update the selected opened mailig list on info window
      //store.dispatch(messagingActions.setMessagingSelectionModel(updatedSelectionModel));
      showUpdateSuccessMessage();
    }
    else {
      showErrorMessage(response.data?.error);
      store.dispatch(commonActions.setIsShowLoadingSpinner(false));
    }
  }).catch(error => {
    showErrorMessage(error);
    store.dispatch(commonActions.setIsShowLoadingSpinner(false));
  });
};

export const deleteCampaign = (campaignData) => {
  messagingActionsService.deleteCampaign(campaignData.id).then((response) => {
    if (response.data.isSuccess) {
      // update all mailing lists
      // update mailing list
      let campaignsData = getMessagingCampaignsData(response.data.entity, campaignData?.site?.[0]);
      store.dispatch(messagingActions.setCampaignsData(campaignsData));
      store.dispatch(messagingActions.setMessagingSelectionModel(null));

      // update the selected opened mailig list on info window
      //store.dispatch(messagingActions.setMessagingSelectionModel(updatedSelectionModel));
      showUpdateSuccessMessage();
    }
    else {
      showErrorMessage(response.data?.error);
      store.dispatch(commonActions.setIsShowLoadingSpinner(false));
    }
  }).catch(error => {
    showErrorMessage(error);
    store.dispatch(commonActions.setIsShowLoadingSpinner(false));
  });
};

export const deletePermission = (userScopeId) => {
  userScopesActionsService.deleteUserScopeRole(userScopeId).then((response) => {
    if (response.data.isSuccess) {
      //store.dispatch(userActions.setShowUserInfoWindow(false));
      if (response.data?.entity) {
        store.dispatch(userActions.deleteGmtUserScopes(response.data));
      }
      showUpdateSuccessMessage();
    } else {
      showErrorMessage(response.data?.error);
    }
  });
};

export const handleEditCampaign = (campaignData) => {
  store.dispatch(
    commonActions.setSideModal({
      modalContent: <AddCampaignForm isEditMode={true} {...campaignData} />,
    })
  );
};

export const handleDuplicateCampaign = (campaignData) => {
  store.dispatch(
    commonActions.setSideModal({
      modalContent: <AddCampaignForm isEditMode={true} isDuplicateMode={true} {...campaignData} />,
    })
  );
};

export const handleDeleteCampaign = (campaignData) => {
  const deleteCampaignMessage = new MessageEntity(
    Icons.DELETE_ITEM_3,
    Strings.TITLES.DELETE_CAMPAIGN,
    Strings.DELETE_CAMPAIGN_MESSAGE,
    Strings.IM_SURE,
    Strings.CANCEL,
    () => deleteCampaign(campaignData)
  );
  store.dispatch(commonActions.setMessageModal(deleteCampaignMessage));
};

export const handleEditMailingList = (listData) => {
  store.dispatch(
    commonActions.setSideModal({
      modalContent: <AddMailingListForm isEditMode={true} {...listData} />,
    })
  );
};

export const deleteList = (listData) => {
  const deleteListMessage = new MessageEntity(
    Icons.DELETE_ITEM_3,
    Strings.TITLES.DELETE_LIST,
    Strings.DELETE_LIST_MESSAGE,
    Strings.IM_SURE,
    Strings.CANCEL,
    () => deleteMailingList(listData)
  );
  store.dispatch(commonActions.setMessageModal(deleteListMessage));
};

export const handleLogout = () => {
  store.dispatch(userActions.logout());
  store.dispatch(commonActions.commonLogout());
};

const base64ToArrayBuffer = (base64) => {
  var binaryString = window.atob(base64);
  var binaryLen = binaryString.length;
  var bytes = new Uint8Array(binaryLen);
  for (var i = 0; i < binaryLen; i++) {
    var ascii = binaryString.charCodeAt(i);
    bytes[i] = ascii;
  }
  return bytes;
};

const saveByteArray = (reportName, byte, saveInStorage = false) => {
  var blob = new Blob([byte], { type: "application/pdf" });
  var link = document.createElement("a");
  link.href = window.URL.createObjectURL(blob);
  var fileName = reportName;
  link.download = fileName;

  return link;
};

const uploadFileToShareStorage = (reportName, byte) => {
  var blob = new Blob([byte], { type: "application/pdf" });
  var guid = createGuid();
  uploadFileToBlob(
    new File([blob], reportName + guid, {
      type: "application/pdf",
    })
  );

  return baseStoragePath + reportName + guid;
};

// Receives base64String with data uri prefix
export const downloadBase64File = (base64String, fileName) => {
    let a = document.createElement("a"); 
    a.href = base64String; 
    a.download = fileName; 
    a.click(); 
};

export const handleDeleteOccupiedToken = (userData, tokenDisplayName, deleteCarUsertokenFn) => {
  const deleteCarUserTokenMessage = new MessageEntity(
    Icons.DELETE_ITEM_3,
    Strings.TITLES.DELETE_CAR_USER_TOKEN,
    `${Strings.DELETE_CAR_USER_TOKEN_MESSAGE}`.format(tokenDisplayName,
      `${userData.firstName} ${userData.lastName}`
    ),
    Strings.IM_SURE,
    Strings.CANCEL,
    deleteCarUsertokenFn
  );
  store.dispatch(commonActions.setMessageModal(deleteCarUserTokenMessage));
};

export const downloadReceiptByInvoiceSummaryId = (invoiceSummariesIds) => {
  //Get Receipt
  actionsService.getReceipt(invoiceSummariesIds).then((response) => {
    if (response.data.isSuccess) {
      response.data.entity?.forEach((file) => {
        if (file?.htmlBase64) {
          var sampleArr = base64ToArrayBuffer(file?.htmlBase64);
          const link = saveByteArray(
            `Utron_Receipt_${file?.invoiceSummaryId.toString()}`,
            sampleArr
          );
          link.click();
        }
      });
    }
  });
};

export const downloadAttachmentByCampaignId = (campaignId, fileName = Strings.EXISTING_FILE_NAME) => {
  messagingActionsService.getCampaignAttachmentById(campaignId)
  .then((response => {
    if (response.data.isSuccess) {
      const base64String = response.data.entity[0];
      downloadBase64File(base64String, fileName);
    }
    else {
      showErrorMessage(response.data?.error);
      store.dispatch(commonActions.setIsShowLoadingSpinner(false));
    }
  })).catch(error => {
    showErrorMessage(error);
    store.dispatch(commonActions.setIsShowLoadingSpinner(false));
  });
};

export const getReceiptUrls = (invoiceSummariesIds) => {
  //Get Receipt
  return actionsService.getReceipt(invoiceSummariesIds).then((response) => {
    let message = "";
    if (response.data.isSuccess) {
      response.data.entity?.forEach((file) => {
        var sampleArr = base64ToArrayBuffer(file?.htmlBase64);
        const link = uploadFileToShareStorage(
          `Utron_Receipt_${file?.invoiceSummaryId.toString()}`,
          sampleArr
        );
        message += `${replaceAll(link, " ", "")}\n`;
      });

      return message;
    }
  });
};

// Receives full file name with extension and returns its extension with lower case
export const getExtensionFileNameToLowerCase = (fileName) => {
  return fileName.replace(fileName.split('.')[1], fileName.split('.')[1].toLowerCase());
};

export const replaceAll = (str, find, replace) => {
  var escapedFind = find.replace(/([.*+?^=!:${}()|\[\]\/\\])/g, "\\$1");
  return str.replace(new RegExp(escapedFind, 'g'), replace);
};

export const updateGmtUserScopes = (userScopes => {
  userScopes.forEach((us => {
    let action = {entity: us};
    store.dispatch(userActions.updateGmtUserScope(action));
  }));
});